import { Link } from "@/lib/router-compat";
import { ArrowRight, Eye, Sparkles, Store, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FrameShapeIllustration } from "@/components/guides/illustrations/FrameShapeIllustration";
import type { FacetEditorial, FacetEditorialCopy } from "@/content/facets";
import {
  FACE_SHAPE_GUIDES,
  FACE_SHAPE_HUB,
  PILLAR_GUIDE,
  guidePath,
} from "@/content/guides/registry";
import { FACE_SHAPES } from "@/content/guides/faceShapes";
import { FACET_PATH_SLUGS, FACET_REGISTRY, getFacetSlug, type FacetKey } from "@/lib/facets";
import type { Lang } from "@/content/guides/types";

interface Ctx {
  countrySlug: string;
  lang: Lang;
  glassesSegment: "lunettes" | "glasses";
}

/* -------------------------------------------------------------------------- */
/* Illustration                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Visuel de la facette : SVG de forme, pastille de couleur ou pictogramme de
 * matière. 100 % vectoriel inline — aucun octet réseau supplémentaire.
 */
export function FacetIllustration({
  editorial,
  label,
  facetKey,
}: {
  editorial: FacetEditorial;
  label: string;
  facetKey: FacetKey;
}) {
  if (editorial.illustration) {
    return (
      <FrameShapeIllustration
        shape={editorial.illustration}
        title={label}
        size={180}
        className="text-primary"
      />
    );
  }

  if (editorial.swatch) {
    return (
      <div
        role="img"
        aria-label={label}
        className="h-20 w-20 rounded-full border border-border shadow-sm"
        style={{ backgroundColor: editorial.swatch }}
      />
    );
  }

  const Icon = facetKey === "material" ? Sparkles : Tag;
  return (
    <div
      role="img"
      aria-label={label}
      className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 text-primary"
    >
      <Icon className="h-8 w-8" aria-hidden />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* « Est-ce fait pour vous ? » + points clés                                   */
/* -------------------------------------------------------------------------- */

export function FacetEditorialSection({
  editorial,
  copy,
  label,
  facetKey,
}: {
  editorial: FacetEditorial;
  copy: FacetEditorialCopy;
  label: string;
  facetKey: FacetKey;
}) {
  return (
    <section className="bg-secondary/40">
      <div className="container py-10">
        <div className="flex flex-col items-start gap-6 md:flex-row md:items-center">
          <div className="shrink-0">
            <FacetIllustration editorial={editorial} label={label} facetKey={facetKey} />
          </div>
          <div className="min-w-0">
            <h2 className="mb-3 text-xl font-bold md:text-2xl">{copy.suitsTitle}</h2>
            <p className="max-w-2xl text-base text-foreground/90">{copy.suits}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FacetHowToChoose({
  copy,
  editorial,
  ctx,
}: {
  copy: FacetEditorialCopy;
  editorial: FacetEditorial;
  ctx: Ctx;
}) {
  const fr = ctx.lang === "fr";
  const faceShapes = editorial.faceShapes ?? [];

  return (
    <section className="container py-10">
      <h2 className="mb-5 text-xl font-bold md:text-2xl">
        {fr ? "Comment bien choisir" : "How to choose well"}
      </h2>

      <div className="grid gap-4 sm:grid-cols-3">
        {copy.keyPoints.map((point) => (
          <div key={point.title} className="rounded-lg border border-border bg-background p-4">
            <h3 className="mb-1 text-sm font-semibold">{point.title}</h3>
            <p className="text-sm text-muted-foreground">{point.body}</p>
          </div>
        ))}
      </div>

      <p className="mt-5 max-w-3xl text-sm text-foreground/90">{copy.advice}</p>

      <div className="mt-5 flex flex-wrap gap-2">
        <Link
          to={guidePath(ctx.countrySlug, PILLAR_GUIDE, ctx.lang)}
          className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-sm transition-colors hover:border-primary hover:text-primary"
        >
          {fr ? "Guide : comment choisir ses lunettes" : "Guide: how to choose your glasses"}
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </Link>
        <Link
          to={guidePath(ctx.countrySlug, FACE_SHAPE_HUB, ctx.lang)}
          className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-sm transition-colors hover:border-primary hover:text-primary"
        >
          {fr ? "Lunettes selon la forme du visage" : "Glasses by face shape"}
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </Link>
        {faceShapes.map((id) => {
          const guide = FACE_SHAPE_GUIDES.find((g) => g.id === `face-${id}`);
          if (!guide) return null;
          return (
            <Link
              key={id}
              to={guidePath(ctx.countrySlug, guide, ctx.lang)}
              className="inline-flex items-center rounded-full bg-secondary px-3 py-1.5 text-sm text-secondary-foreground transition-colors hover:bg-primary hover:text-primary-foreground"
            >
              {fr
                ? `Visage ${FACE_SHAPES[id].label.fr.toLowerCase()}`
                : `${FACE_SHAPES[id].label.en} face`}
            </Link>
          );
        })}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Verres & prix                                                               */
/* -------------------------------------------------------------------------- */

export function FacetPriceSection({
  ctx,
  priceLabel,
}: {
  ctx: Ctx;
  priceLabel?: string | undefined;
}) {
  const fr = ctx.lang === "fr";
  return (
    <section className="bg-primary/5">
      <div className="container py-10">
        <h2 className="mb-3 text-xl font-bold md:text-2xl">
          {fr ? "Verres, prix et test de vue" : "Lenses, prices and eye test"}
        </h2>
        <p className="max-w-3xl text-base text-foreground/90">
          {fr
            ? `Chez Lapaire, le prix affiché correspond à la monture${priceLabel ? ` (à partir de ${priceLabel})` : ""} ; les verres correcteurs s'ajoutent selon votre prescription : simple foyer, progressif, anti-lumière bleue ou solaire. Le test de vue reste gratuit en boutique, avec ou sans achat.`
            : `At Lapaire, the displayed price is for the frame${priceLabel ? ` (from ${priceLabel})` : ""}; prescription lenses are added according to your prescription: single vision, progressive, blue-light or sun. The eye test stays free in store, with or without a purchase.`}
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Button asChild>
            <Link to={`/${ctx.countrySlug}/book`}>
              <Eye className="mr-2 h-4 w-4" aria-hidden />
              {fr ? "Réserver un test de vue gratuit" : "Book a free eye test"}
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to={`/${ctx.countrySlug}/prices`}>
              {fr ? "Voir tous les prix" : "See all prices"}
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to={`/${ctx.countrySlug}/stores`}>
              <Store className="mr-2 h-4 w-4" aria-hidden />
              {fr ? "Trouver une boutique" : "Find a store"}
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Maillage croisé entre familles de facettes                                  */
/* -------------------------------------------------------------------------- */

function facetValueLabel(key: FacetKey, valueId: string, lang: Lang): string | null {
  const value = FACET_REGISTRY[key].find((v) => v.id === valueId);
  if (!value) return null;
  return lang === "fr" ? value.label_fr : value.label_en;
}

function facetValuePath(key: FacetKey, valueId: string, ctx: Ctx): string | null {
  const value = FACET_REGISTRY[key].find((v) => v.id === valueId);
  if (!value) return null;
  return `/${ctx.countrySlug}/${ctx.glassesSegment}/${FACET_PATH_SLUGS[ctx.lang][key]}/${getFacetSlug(value, ctx.lang)}`;
}

export function FacetCrossLinks({
  editorial,
  ctx,
}: {
  editorial: FacetEditorial;
  ctx: Ctx;
}) {
  const fr = ctx.lang === "fr";
  const related = editorial.related ?? [];
  if (related.length === 0) return null;

  return (
    <section className="container py-8">
      <h2 className="mb-3 text-lg font-bold">
        {fr ? "Ces sélections vont souvent ensemble" : "These selections often go together"}
      </h2>
      <div className="flex flex-wrap gap-2">
        {related.map(({ key, valueId }) => {
          const label = facetValueLabel(key, valueId, ctx.lang);
          const path = facetValuePath(key, valueId, ctx);
          if (!label || !path) return null;
          return (
            <Link
              key={`${key}-${valueId}`}
              to={path}
              className="inline-flex items-center rounded-full border border-border bg-background px-3 py-1.5 text-sm transition-colors hover:border-primary hover:text-primary"
            >
              {fr ? `Lunettes ${label}` : `${label} glasses`}
            </Link>
          );
        })}
      </div>
    </section>
  );
}
